import { fetchApi } from '@/services/api';

export type LogisticsUnitKind = 'PALLET' | 'CONTAINER' | 'TRUCK';
export type LogisticsAssignmentRole = 'DEFAULT_CONTAINER' | 'DEFAULT_PALLET' | 'ALLOWED';

export interface LogisticsUnitTemplate {
  id: number;
  code: string;
  name: string;
  description: string;
  kind: LogisticsUnitKind;
  active: boolean;
  internal_length_mm: number | null;
  internal_width_mm: number | null;
  internal_height_mm: number | null;
  theoretical_volume_m3: number | null;
  volume_utilization_factor: number | null;
  usable_volume_m3: number | null;
  max_payload_kg: number | null;
  weight_utilization_factor: number | null;
  usable_payload_kg: number | null;
  minimum_fill_pct: number | null;
  maximum_fill_pct: number | null;
  capacity_binding_rule: string;
  notes: string;
  updated_at?: string | null;
}

export interface LogisticsUnitAssignment {
  id: number;
  supplier_id: number;
  supplier_code: string;
  supplier_name: string;
  template_id: number;
  template_code: string;
  template_name?: string;
  template_kind: string;
  template_usable_volume_m3?: number | null;
  template_usable_payload_kg?: number | null;
  template_minimum_fill_pct?: number | null;
  role: LogisticsAssignmentRole;
  priority: number;
  active: boolean;
}

export type LogisticsUnitTemplatePayload = Partial<
  Omit<LogisticsUnitTemplate, 'id' | 'usable_volume_m3' | 'usable_payload_kg' | 'updated_at'>
> & { code?: string };

export const logisticsCatalogService = {
  listTemplates: async (params?: {
    kind?: LogisticsUnitKind;
    search?: string;
    include_inactive?: boolean;
  }): Promise<LogisticsUnitTemplate[]> => {
    const q = new URLSearchParams();
    if (params?.kind) q.set('kind', params.kind);
    if (params?.search) q.set('search', params.search);
    if (params?.include_inactive) q.set('include_inactive', 'true');
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const res = await fetchApi<{ results: LogisticsUnitTemplate[] }>(
      `supply-agent/logistics/unit-templates/${suffix}`
    );
    return res.results || [];
  },

  createTemplate: async (payload: LogisticsUnitTemplatePayload): Promise<LogisticsUnitTemplate> => {
    const res = await fetchApi<{ template: LogisticsUnitTemplate }>(
      'supply-agent/logistics/unit-templates/',
      { method: 'POST', body: payload }
    );
    return res.template;
  },

  updateTemplate: async (
    id: number,
    payload: LogisticsUnitTemplatePayload
  ): Promise<LogisticsUnitTemplate> => {
    const res = await fetchApi<{ template: LogisticsUnitTemplate }>(
      `supply-agent/logistics/unit-templates/${id}/`,
      { method: 'PATCH', body: payload }
    );
    return res.template;
  },

  deactivateTemplate: async (id: number): Promise<void> => {
    await fetchApi(`supply-agent/logistics/unit-templates/${id}/`, { method: 'DELETE' });
  },

  listAssignments: async (params?: {
    template_id?: number;
    supplier_id?: number;
    include_inactive?: boolean;
  }): Promise<LogisticsUnitAssignment[]> => {
    const q = new URLSearchParams();
    if (params?.template_id) q.set('template_id', String(params.template_id));
    if (params?.supplier_id) q.set('supplier_id', String(params.supplier_id));
    if (params?.include_inactive) q.set('include_inactive', 'true');
    const suffix = q.toString() ? `?${q.toString()}` : '';
    const res = await fetchApi<{ results: LogisticsUnitAssignment[] }>(
      `supply-agent/logistics/unit-assignments/${suffix}`
    );
    return res.results || [];
  },

  createAssignment: async (payload: {
    supplier_id: number;
    template_id: number;
    role: LogisticsAssignmentRole;
    priority?: number;
  }): Promise<LogisticsUnitAssignment> => {
    const res = await fetchApi<{ assignment: LogisticsUnitAssignment }>(
      'supply-agent/logistics/unit-assignments/',
      { method: 'POST', body: payload }
    );
    return res.assignment;
  },

  deactivateAssignment: async (id: number): Promise<void> => {
    await fetchApi(`supply-agent/logistics/unit-assignments/${id}/`, { method: 'DELETE' });
  },
};
